import React, { useState } from 'react';
import { Lock, Map, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Footer from '../components/accueil/Footer';
import ChatWidget from '../components/accueil/ChatWidget';
import Navbar from '../components/accueil/Navbar';
import PageBanner from './Banner';
import api from '../components/api';
import useGlobale from '../components/global';

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { authenticated, user, logout, login } = useGlobale();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const cleanPhone = phone.replace(/\D/g, '');
      const response = await api.post('/utilisateur/signin/', {
        username: cleanPhone,
        password: password
      });

      if (response.status === 200) {
        const userData = response.data;
        // console.log('Données de connexion reçues:', userData);

        const authData = {
          access: userData.tokens.access,    // Changé ici
          refresh: userData.tokens.refresh   // Changé ici
        };
        localStorage.setItem('auth_tokens', JSON.stringify(authData));

        // Vérification du stockage
        const storedTokens = localStorage.getItem('auth_tokens');
        // console.log('Tokens stockés:', JSON.parse(storedTokens));

        // Login dans le store global avec la bonne structure
        await login({
          access: userData.tokens.access,    
          refresh: userData.tokens.refresh   
        }, userData.user);

        navigate('/');
      }
    } catch (error) {
      // console.error('Erreur complète:', error);
      if (error.response) {
        switch (error.response.status) {
          case 400:
            setError('Veuillez remplir tous les champs correctement');
            break;
          case 401:
            setError('Identifiants incorrects');
            break;
          default:
            setError('Une erreur est survenue lors de la connexion' );
        }
      } else if (error.request) {
        setError('Le serveur ne répond pas, veuillez réessayer plus tard' );
      } else {
        setError('Impossible d\'établir la connexion au serveur');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Navbar authenticated={authenticated} user={user} logout={logout} />
      <PageBanner currentPage="Connexion" />
      <div className="min-h-[calc(100vh-450px)] flex items-center justify-center bg-gray-50 py-8">
        <div className="max-w-md w-full space-y-6 bg-white p-6 rounded-2xl shadow-xl mx-4">
          {/* En-tête */}
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-brand-600 p-3 rounded-xl shadow-lg">
                <Map className="h-8 w-8 text-white" />
              </div>
            </div>
            <h2 className="mt-2 text-2xl font-bold text-gray-900">
              Bienvenue
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Connectez-vous à votre compte Niger Voyage
            </p>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {/* Numéro de téléphone */}
            <div>
              <label htmlFor="phone" className="text-sm font-medium text-gray-700 block mb-2">
                Numéro de téléphone
              </label>
              <PhoneInput
                country={'ne'}
                value={phone}
                onChange={value => setPhone(value)}
                inputProps={{
                  required: true,
                  name: 'phone',
                  id: 'phone'
                }}
                containerClass="!w-full"
                inputClass="!w-full !h-[46px] !text-base !pl-12"
                buttonClass="!h-[46px]"
                searchPlaceholder="Rechercher un pays"
                preferredCountries={['ne', 'bj', 'bf', 'ml', 'ng', 'td']}
              />
              <p className="text-xs text-gray-500 mt-1">
                Cliquez sur le drapeau pour choisir l'indicatif du numéro
              </p>
            </div>

            {/* Mot de passe */}
            <div>
              <label htmlFor="password" className="text-sm font-medium text-gray-700 block mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl 
                            focus:ring-2 focus:ring-brand-500 focus:border-brand-500
                            transition-all duration-200 ease-in-out
                            text-gray-900 placeholder-gray-400"
                  placeholder="Votre mot de passe"
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOffIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {/* Options de connexion */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Se souvenir de moi
                </label>
              </div>
              <a href="/reset-password" className="text-sm font-medium text-brand-600 hover:text-brand-700">
                Mot de passe oublié ?
              </a>
            </div>

            {/* Bouton de connexion */}
            <div>
              <button
                type="submit"
                disabled={isLoading || !phone || !password}
                className={`group relative w-full flex justify-center py-3 px-4 
                            border border-transparent rounded-xl text-white 
                            bg-brand-600 hover:bg-brand-700
                            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500
                            transition-all duration-200 ease-in-out
                            font-medium text-sm
                            ${(isLoading || !phone || !password) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Se connecter'
                )}
              </button>
            </div>

            {/* Lien d'inscription */}
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Nouveau sur Niger Voyage ?{' '}
                <a href="/inscription" className="font-medium text-brand-600 hover:text-brand-700">
                  Créer un compte
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
      <Footer />
      <ChatWidget />

      {/* Styles pour PhoneInput */}
      <style jsx global>{`
        .react-tel-input .form-control {
          width: 100% !important;
          height: 46px !important;
          border-radius: 0.75rem !important;
          font-size: 1rem !important;
        }
        
        .react-tel-input .flag-dropdown {
          border-radius: 0.75rem 0 0 0.75rem !important;
          border-color: #e5e7eb !important;
        }
        
        .react-tel-input .selected-flag {
          border-radius: 0.75rem 0 0 0.75rem !important;
          background-color: transparent !important;
        }

        .react-tel-input .selected-flag:hover,
        .react-tel-input .selected-flag:focus {
          background-color: transparent !important;
        }

        .react-tel-input .country-list {
          border-radius: 0.75rem !important;
          margin-top: 8px !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
        }
      `}</style>
    </>
  );
};

export default LoginPage;